/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Button, Dropdown, message, Modal, notification } from "antd";
import { Link, useParams } from "react-router-dom";
import { MoreVertical, Terminal } from "lucide-react";
import moment from "moment-timezone";
import { useDebounce } from "../../../utils/hooks";
import { AUTH_TOKEN, CustomTagRender, DOMAIN_ID, getUserName, ORG_ID, isEmpty } from "../../../utils/helpers";
import { AppContentLayout, PageSection } from "../../../layout/applayout";
import { FilterComponents } from "../../../components/ui/common";
import { appAxios, getConnectionMode } from "../../../service/appService";
import { JUMPSERVER_CONNECTION_PORT, REACT_APP_API } from "../../../constants";
import { getInstanceList, getListConnections } from "../../../service/pamService";
import { getWalletUserList } from "../../../service/didService";
import DataGridTable from "../../../components/ui/datatable/DataGridTable";
import { TimezoneRender } from "../../../components/ui/datatable/DataTableContent";

import copyText from "../../../utils/copyText";
const Connection = () => {
  let userEmail = getUserName();
  const { publicIpAddress } = useParams();
  const [messageApi, contextHolder] = message.useMessage();
  const [notificationApi, contextHolderNotification] = notification.useNotification();
  const [connectionData, setConnectionData] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [userData, setUserData] = useState([]);
  const [connectionMode, setConnectionMode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const formatString = "MM-DD-YYYY";

  //pagination
  const PAGESIZE = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationParams, setPaginationParams] = useState({
    page: 0,
    pageSize: PAGESIZE,
  });

  // UI configuration
  const filterByOptions = useMemo(() => {
    const option = [
      {
        value: "endpoint",
        label: "Endpoints",
        children: endpoints,
      },
      {
        value: "walletuser",
        label: "User email",
        children: userData,
      },
      {
        value: "protocol",
        label: "Protocol",
        children: [
          {
            value: "SSH",
            label: "SSH",
          },
          {
            value: "RDP",
            label: "RDP",
          },
          {
            value: "VNC",
            label: "VNC",
          },
          {
            value: "Telnet",
            label: "Telnet",
          },
        ],
      },
    ];
    return option;
  }, [endpoints, userData]);

  /// DataTable
  const tableStructure = useMemo(() => {
    const baseColumns = [
      { key: "publicIpAddress", field: "publicIpAddress", headerName: "Public IP" },
      { key: "privateIpAddress", field: "privateIpAddress", headerName: "Private IP" },
      { key: "user", field: "user", headerName: "User" },
      {
        key: "walletuser",
        field: "walletuser",
        headerName: "User email",
        renderCell: (val) => <CustomTagRender value={val.row.walletuser || "-"} />,
      },
      { key: "protocol", field: "protocol", headerName: "Protocol" },
      {
        key: "endpoints",
        field: "endpoints",
        headerName: "Endpoints",
        renderCell: (val) => (
          <>
            {val.row.endpoints.map((tag, index) => (
              <CustomTagRender key={`${tag.hostname}-${index}`} value={tag.hostname} />
            ))}
          </>
        ),
      },
      {
        key: "expireAt",
        field: "expireAt",
        headerName: "Expire At",
        renderCell: (val) => <TimezoneRender rowValue={val.row} keyValue="expireAt" />,
      },
      {
        key: "action",
        headerName: "Actions",
        field: "action",
        renderCell: (record) => {
          const items = [];

          if (connectionMode !== 1 && record.row.protocol === "RDP") {
            items.push({
              key: "2",
              flex: 1,
              label: (
                <Link
                  className="!text-primary !font-semibold !underline"
                  onClick={() => {
                    return window.open(`${REACT_APP_API}/api/v1/secretservice/generateRDPFile?machineName=${record?.row?.endpoints[0]?.hostname}&userName=${record?.row?.user}&orgId=${ORG_ID()}`);
                  }}
                >
                  Download RDP
                </Link>
              ),
            });
          }

          if (record.row.protocol === "SSH") {
            items.push({
              key: "3",
              flex: 1,
              label: (
                <Link
                  className="!text-primary !font-semibold !underline"
                  onClick={() => {
                    fetchSSHCommands(record?.row?.user, record?.row?.endpoints[0]?.hostname);
                  }}
                >
                  SSH Command
                </Link>
              ),
            });
          }

          if (!items.length) return "-";

          return (
            <Dropdown
              placement="bottomLeft"
              menu={{
                items: items,
              }}
            >
              <Button type="text" shape="circle" icon={<MoreVertical className="!size-4" />} />
            </Dropdown>
          );
        },
      },
    ];

    if (connectionMode !== 2) {
      const connectColumn = {
        field: "connect",
        headerName: "Connect",
        key: "connect",
        minWidth: 150,
        renderCell: (val) => {
          const domainIp = val?.row?.connectBy === 0 ? val?.row?.publicIpAddress : val?.row?.privateIpAddress;

          const endpointDomainIp = val?.row?.connectBy === 0 ? val?.row?.endpoints?.[0]?.publicIpAddress : val?.row?.endpoints?.[0]?.privateIpAddress;

          return (
            <>
              <div
                className="flex items-center justify-center cursor-pointer bg-green-700 px-2 py-1 rounded-sm !text-xs space-x-1 text-white hover:text-black hover:bg-transparent hover:border hover:border-green-700"
                onClick={() => {
                  notificationApi.info({
                    message: "Entering Servlet:",
                    description: "Please approve credentials to proceed and Session will be recorded.",
                    placement: "topRight",
                  });
                  window.open(`http://${domainIp}:${JUMPSERVER_CONNECTION_PORT}/guacamole-servlet/?orgId=${ORG_ID()}&tenantId=${DOMAIN_ID()}&hostName=${endpointDomainIp}&userName=${val.row.user}&ipAddress=localhost&bearerToken=${AUTH_TOKEN()}&loggedInUser=${userEmail}&protocol=${val.row.protocol}&hostlabel=${val.row.endpoints[0].hostname}`, "_blank", "width=1024,height=768");
                }}
              >
                <Terminal className="icon !size-4" />
                <span>Connect Now</span>
              </div>
            </>
          );
        },
      };

      // Insert connect column at index 6
      return [...baseColumns.slice(0, 6), connectColumn, ...baseColumns.slice(6)];
    }

    return baseColumns;
  }, [connectionMode, messageApi]);

  /// Filter
  const [search, setSearch] = useState("");
  const debouncedValue = useDebounce(search, 600);
  const [filterParams, setFilterParams] = useState({
    search: "",
    filter: {
      filterBy: publicIpAddress ? "publicipaddress" : "",
      value: publicIpAddress ? publicIpAddress : "",
    }
  });

  const handleChangeAutocomplete = (value, name) => {
    setSearch(value);
    setFilterParams((prev) => ({ ...prev, [name]: value }));
    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isAutocomplete && item?.inputName === name) return { ...item, inputValue: value };
        return item;
      });
    });
  };

  useEffect(() => {
    const filteredOptions = isEmpty(search)
      ? []
      : connectionData
        .filter((connectiondata) => connectiondata.user.toLowerCase().includes(search.toLowerCase()))
        .map((connectiondata) => ({
          label: connectiondata.user,
          value: connectiondata.user,
        }));

    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isAutocomplete) return { ...item, results: filteredOptions };
        return item;
      });
    });
  }, [search]);

  const [filterstate, setFilterState] = useState([
    // { isFilter: true, labelName: "Filter by", inputName: "filter", placeholder: "Select to search", options: [], handleOnChange: null, handleOnClear: null },
    {
      isAutocomplete: true, inputValue: "", placeholder: "Search by user", inputName: 'search', labelName: "Search", handleSearch: (value) => setSearch(value), results: [], handleOnChange: null, handleOnClear: null
    },
  ]);

  const handleOnClear = (name) => {
    const value = name === 'filter' ? { filterBy: "", value: "" } : ""
    setFilterParams((prev) => ({ ...prev, [name]: value }));
  }

  const handleFilterBy = (data, name) => {
    setFilterParams((prev) => ({ ...prev, [name]: { filterBy: data ? data[0]?.value : "", value: data ? data[1]?.value : "" } }));
  };


  // lifeCycle hooks
  useEffect(() => {
    fetchInstance();
    fetchUsers();
    fetchConnectionMode();
    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isAutocomplete) return { ...item, handleOnChange: handleChangeAutocomplete, handleOnClear }
        if (item?.isFilter) return { ...item, handleOnChange: handleFilterBy, handleOnClear }
        return item;
      })
    })
  }, []);

  useEffect(() => {
    setFilterState((prevState) => {
      return prevState?.map((item) => {
        if (item?.isFilter) return { ...item, options: filterByOptions };
        return item;
      });
    });
  }, [userData, endpoints]);

  useEffect(() => {
    fetchConnection();
  }, [debouncedValue, filterParams, paginationParams]);

  useEffect(() => {
    if (paginationParams.page !== 0) {
      setPaginationParams({ page: 0, pageSize: PAGESIZE })
    }
  }, [filterParams])


  // API calls
  const fetchConnection = () => {
    let payload = {
      pageId: paginationParams.page + 1,
      pageSize: paginationParams.pageSize,
      search: filterParams?.search,
      filter: {
        filterBy: filterParams?.filter?.filterBy,
        value: `${filterParams?.filter?.value}`,
      },
      endpoints: filterParams.filter.filterBy === "endpoint" ? filterParams.filter.value : [],
      token: AUTH_TOKEN(),
      domainId: DOMAIN_ID(),
      orgId: ORG_ID(),
      date: moment().format(formatString),
    };
    setIsLoading(true);
    getListConnections({ payload, onSuccess: handleConnectionSuccess, onFailure: handleConnectionFailure });
  };

  const handleConnectionSuccess = (data) => {
    const connectionData = data?.jumpServerConnections.map((data, index) => {
      return {
        ...data,
        status: data?.endpoints?.map((i) => (i.status === false ? "Inactive" : "Active")),
        id: index + 1,
      };
    });
    setConnectionData(connectionData ?? []);
    setTotalCount(data?.total || connectionData.length);
    setTimeout(() => setIsLoading(false), 500);
  };

  const handleConnectionFailure = useCallback(() => {
    setIsLoading(false);
  }, []);

  const fetchUsers = () => {
    let payload = {
      domainId: DOMAIN_ID(),
      orgId: ORG_ID(),
      pageNumber: 1,
      pageSize: 500,
      filter: "",
    };
    setIsLoading(true);
    getWalletUserList({ payload, onSuccess: handleUserListSuccess, onFailure: handleUserListFailure });
  };

  const handleUserListSuccess = (data) => {
    setIsLoading(false);
    const Data = data.users.map((user, i) => ({
      value: user.emailAddress,
      label: user.emailAddress ? user.emailAddress : "-",
    }));
    setUserData(Data);
  };

  const handleUserListFailure = () => {
    setIsLoading(false);
    messageApi.open({
      type: "error",
      content: "Unable to fetch users",
    });
  };

  const fetchInstance = () => {
    let payload = {
      domainId: DOMAIN_ID(),
      pageId: 1,
      pageSize: 500,
      search: "",
      filter: {
        filterBy: "",
        value: "",
      },
      token: AUTH_TOKEN(),
      orgId: ORG_ID(),
      MachineType: "endpoint",
    };
    getInstanceList({ payload, onSuccess: handleInstanceSuccess, onFailure: handleInstanceFailure });
  };

  const handleInstanceSuccess = (data) => {
    setEndpoints(
      data?.instances.map((ins) => ({
        value: ins.hostName,
        label: (
          <div>
            {ins.hostName} <b>{`(${ins.publicIp})`}</b>
          </div>
        ),
        ipAddress: ins.publicIpAddress,
      }))
    );
  };

  const handleInstanceFailure = useCallback(() => { }, []);

  const fetchConnectionMode = async () => {
    let payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
    };
    getConnectionMode({ payload, onSuccess: handleConnectionModeSuccess, onFailure: handleConnectionModeFailure });
  };

  const handleConnectionModeSuccess = useCallback((data) => {
    setConnectionMode(data.mode);
  }, []);

  const handleConnectionModeFailure = useCallback(() => { }, []);

  const fetchSSHCommands = async (user, machineName) => {
    let payload = {
      orgId: ORG_ID(),
      machineName: machineName,
      username: user,
    };
    try {
      setIsLoading(true);
      const res = await appAxios.post(`/api/v1/secretservice/generateSSHcommand`, payload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      });
      setModalContent(res?.data?.command);
      setIsModalOpen(true);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("fetchSSHCommands failed:", error?.response?.status, error?.message);
    }
  };

  // Methods and handlers
  return (
    <>
      {contextHolder}
      {contextHolderNotification}
      {PopupModal(isModalOpen, () => setIsModalOpen(false), modalContent, messageApi)}
      <AppContentLayout
        title={<PageSection screenName="Connections" screenDescription={"Manage your connections"} />}
        cardChildren={
          <>
            <div className="px-6 py-4">
              <FilterComponents uiStructure={filterstate} />
              <DataGridTable
                tableStructure={tableStructure}
                data={connectionData}
                loading={isLoading}
                totalCount={totalCount}
                pagination={paginationParams}
                handleChangePagination={
                  ({ page, pageSize }) => { setPaginationParams({ page, pageSize }); }
                }
              />
            </div>
          </>
        }
      />
    </>
  );
};

const PopupModal = (isModalOpen, handleCancel, content, isNotification) => {
  const handleCopy = async (textToCopy) => {
    try {
      await copyText(textToCopy);
      handleCancel();
      isNotification.open({
        type: "success",
        content: "Copied to clipboard",
      });
    } catch (err) {
      isNotification.open({
        type: "error",
        content: "Failed to copy",
      });
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <Modal title="SSH Command" open={isModalOpen} okText="Copy" onOk={() => handleCopy(content)} onCancel={handleCancel} centered={true} width="70%">
      <span>{content ? content : "No SSH Command"}</span>
    </Modal>
  );
};

export default Connection;
